import type { BacktestTrade } from "@/api/types";
import type { Fill } from "@/lib/preview-stream/previewStreamDecoder";
import { timeframeMinutes } from "@/lib/new-backtest";

/** Candles the chart shows first, while the full history is still streaming. */
export const TAIL_BARS = 1500;

/** "YYYY-MM-DD HH:MM:SS" or ISO, read as UTC. */
function utcMs(value: string | null | undefined): number {
  if (!value) return NaN;
  const s = value.trim().replace(" ", "T");
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s.length <= 10 ? `${s}T00:00:00` : s}Z`);
}

function fmt(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:00`;
}

/**
 * Start date of a short stream covering only the newest candles, or null when
 * the run is short enough that the full stream is just as quick.
 */
export function tailStartDate(startDate: string | undefined, timeframe: string | undefined, now = Date.now()): string | null {
  const mins = timeframeMinutes(timeframe || "");
  const start = utcMs(startDate);
  if (!mins || !Number.isFinite(start)) return null;
  const tfMs = mins * 60_000;
  if ((now - start) / tfMs < TAIL_BARS * 4) return null;
  return fmt(now - TAIL_BARS * tfMs);
}

/**
 * Chart markers for the short stream, taken from the finished backtest: the
 * short stream starts late, so its own trades are not the backtest's trades.
 * Each fill is moved onto the candle it happened in.
 */
export function fillsFromTrades(trades: BacktestTrade[] | undefined, barTimes: ArrayLike<number>, nBars: number): Fill[] {
  if (!trades?.length || nBars === 0) return [];
  const first = barTimes[0]!;
  const out: Fill[] = [];
  for (const t of trades) {
    const ms = utcMs(t.date);
    if (!Number.isFinite(ms) || ms < first) continue;
    // Last candle that opened at or before the fill.
    let lo = 0;
    let hi = nBars - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (barTimes[mid]! <= ms) lo = mid;
      else hi = mid - 1;
    }
    const dir = String(t.direction || "");
    const closing = dir.startsWith("close");
    const buy = dir ? dir === "long" || dir === "close_short" : String(t.action).toLowerCase() === "buy";
    const qty = Math.abs(Number(t.crypto) || 0) || 1;
    out.push({ kind: closing ? "closed" : "opened", time: barTimes[lo]!, price: t.price, qty: buy ? qty : -qty, fee: t.fee });
  }
  return out.sort((a, b) => a.time - b.time);
}

/** Start date of a stream that brings the last `bars` candles of a timeframe; null when the timeframe is unknown. */
export function barsAgoDate(timeframe: string | undefined, bars: number, now = Date.now()): string | null {
  const mins = timeframeMinutes(timeframe || "");
  return mins ? fmt(now - bars * mins * 60_000) : null;
}
